import { ComparatorType } from "./interface";
import { COMPANION_TYPE_CONFIG } from "./VariableDto";
import { WhereClauseOp } from "./WhereClauseOp";

/**
 * Generate preview text for a single where clause operation.
 *
 * @param whereClause - The where clause to generate a preview for
 * @param comparatorTypes - Available comparator types used to determine formatting
 * @returns Preview text string
 */
const generateSingleClausePreviewFromOp = (
  whereClause: WhereClauseOp,
  comparatorTypes: ComparatorType[],
): string => {
  if (!whereClause.left?.dataset || !whereClause.left) {
    return "";
  }

  let text = `${whereClause.left.dataset}.${whereClause.left.nameVariable}`;

  // Add companions in the order they appear in the array
  if (whereClause.left.companionVariables && whereClause.left.companionVariables.length > 0) {
    whereClause.left.companionVariables.forEach((companion) => {
      const formatFn = COMPANION_TYPE_CONFIG[companion.type];
      text += formatFn(companion.variable);
    });
  }

  if (!whereClause.comparator) {
    return text;
  }

  // Look up the full comparator object by name
  const comparatorType = comparatorTypes.find((c) => c.name === whereClause.comparator);
  if (!comparatorType) {
    return text;
  }

  // Check if comparator doesn't require right-hand side value
  if (comparatorType.rhsType === "None") {
    text += ` ${comparatorType.denotation}`;
  } else if (whereClause.right) {
    if (comparatorType.rhsType === "Group") {
      // For group comparators, parse values separated by newlines
      const values = whereClause.right
        .split("\n")
        .map((v) => v.trim())
        .filter((v) => v !== "");
      const isNumeric = values.every((v) => /^\d+(\.\d+)?$/.test(v));
      const formattedValues = values.map((v) => (isNumeric ? v : `"${v}"`)).join(", ");
      text += ` ${comparatorType.denotation} (${formattedValues})`;
    } else {
      // For single value comparators
      const isNumeric = /^\d+(\.\d+)?$/.test(whereClause.right);
      const formattedValue = isNumeric ? whereClause.right : `"${whereClause.right}"`;
      text += ` ${comparatorType.denotation} ${formattedValue}`;
    }
  }

  return text;
};

/**
 * Generate preview text for multiple where clauses joined by their logical operators.
 *
 * @param whereClauses - Array of where clauses
 * @param comparatorTypes - Available comparator types used to determine formatting
 * @returns Combined preview text for display
 */
export const generatePreviewText = (
  whereClauses: WhereClauseOp[],
  comparatorTypes: ComparatorType[],
): string => {
  const previews = whereClauses
    .map((clause, idx) => {
      const clausePreview = generateSingleClausePreviewFromOp(clause, comparatorTypes);
      if (clausePreview === "") return "";
      // Only show precedingLogic for clauses after the first
      const logicText = idx > 0 && clause.precedingLogic ? `${clause.precedingLogic} ` : "";
      return `${logicText}${clausePreview}`;
    })
    .filter((preview) => preview !== "");

  return previews.join(" ");
};
